/**
 * PhotoVogue admin/editorial domain types.
 *
 * Hand-written (NOT codegen) so Step 1 stays free of GraphQL documents — see the
 * plan's "No GraphQL documents in Step 1" decision. These mirror `photovogue-api`'s
 * Photo/Photographer model closely enough that Step 2 can back them with real
 * REST/GraphQL responses behind the same `PhotoVogueService` interface.
 *
 * Intentionally decoupled from the Open Calls (`@repo/api/open-calls`) domain.
 */

// ---------------------------------------------------------------------------
// Photo
// ---------------------------------------------------------------------------

export type PhotoState =
  | 'uploaded'
  | 'ready'
  | 'approved'
  | 'rejected'
  | 'banned'
  | 'to_fix';

export type PhotoType = 'best' | 'daily';

export interface PhotoImageUrls {
  thumbnail: string;
  small: string;
  /** Uncropped small derivative used by moderation surfaces. */
  smallFit: string;
  medium: string;
  large: string;
  gallery: string;
  private: string;
}

export interface PhotoCropInfo {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  height: number;
}

export interface PhotoPhotographerRef {
  id: string;
  name: string;
}

export interface Photo {
  id: string;
  title: string;
  description: string;
  state: PhotoState;
  category: string;
  tags: string[];
  themes: string[];
  types: PhotoType[];
  crop: PhotoCropInfo | null;
  photographer: PhotoPhotographerRef;
  imageUrls: PhotoImageUrls;
  createdAt: string;
  stateUpdatedAt: string | null;
}

// ---------------------------------------------------------------------------
// Photographer
// ---------------------------------------------------------------------------

export type PhotographerState =
  | 'valid'
  | 'banned-temporarily'
  | 'banned-permanently'
  | 'to_fix'
  | 'invalid';

export type PhotographerTier = 'basic' | 'gold';

export interface Photographer {
  createdAt?: string | null;
  id: string;
  name: string;
  avatar: string;
  city: string;
  country: string;
  state: PhotographerState;
  tier: PhotographerTier;
  warningCount: number;
  banCount: number | null;
  totalPhotos: number;
  totalBest: number;
  totalDaily: number;
  email?: string | null;
  contact?: string | null;
  contactStatus?: 'available' | 'missing' | 'unavailable';
  categories?: string[];
  themes?: string[];
  agents?: string[];
  biography?: string;
  websites?: string[];
}

// ---------------------------------------------------------------------------
// Query params / responses (local, decoupled from open-calls graphql)
// ---------------------------------------------------------------------------

export interface Pagination {
  page: number;
  limit: number;
  skip: number;
}

export type SortDirection = 'asc' | 'desc';

export interface PhotoSort {
  field: 'createdAt' | 'stateUpdatedAt' | 'title';
  direction: SortDirection;
}

export interface PhotoFilters {
  states?: PhotoState[];
  category?: string;
  tags?: string[];
  themes?: string[];
  type?: PhotoType;
  /** Exclude an editorial type, used to keep existing Daily photos out of the picker. */
  excludeType?: PhotoType;
  photographerId?: string;
  search?: string;
  /** Upload-date range (inclusive), yyyy-MM-dd, matched against `createdAt`. */
  uploadedFrom?: string;
  uploadedTo?: string;
}

export interface PhotoQueryParams {
  filters: PhotoFilters;
  pagination: Pagination;
  sort: PhotoSort;
}

export interface PhotosResponse {
  data: Photo[];
  _ids: string[];
  count: number;
}

export interface PhotographerFilters {
  states?: PhotographerState[];
  tier?: PhotographerTier;
  country?: string;
  search?: string;
  /** When true, exclude photographers whose canonical photo count is zero. */
  withPhotosOnly?: boolean;
}

export interface PhotographerSort {
  field: 'name' | 'totalPhotos';
  direction: SortDirection;
}

export interface PhotographerQueryParams {
  filters: PhotographerFilters;
  pagination: Pagination;
  sort: PhotographerSort;
}

export interface PhotographersResponse {
  data: Photographer[];
  _ids: string[];
  count: number;
}

export interface PhotoVogueFilterOptions {
  categories: string[];
  themes: string[];
  tags: string[];
  countries: string[];
}

// ---------------------------------------------------------------------------
// Moderation
// ---------------------------------------------------------------------------

/** Partial photo edit — metadata, tags, types (Best/Daily), or state. */
export interface PhotoPatch {
  title?: string;
  description?: string;
  category?: string;
  tags?: string[];
  themes?: string[];
  types?: PhotoType[];
  state?: PhotoState;
}

/** A Pic-of-the-Day assignment: one photo featured on a calendar date. */
export interface DailyAssignment {
  /** yyyy-MM-dd */
  date: string;
  photo: Photo;
}

export interface PhotoModerationPatch {
  state: PhotoState;
}

export interface PhotoBatchPatch extends PhotoModerationPatch {
  id: string;
  types: PhotoType[];
}

/** Result of a batch moderation call — ids that failed to persist. */
export interface BatchResult {
  failed: string[];
}

export type ModerationAction = 'approve' | 'reject' | 'ban' | 'to_fix';
export type BulkPhotoAction = ModerationAction | 'best';

export const MODERATION_ACTION_TO_STATE: Record<ModerationAction, PhotoState> =
  {
    approve: 'approved',
    reject: 'rejected',
    ban: 'banned',
    to_fix: 'to_fix',
  };

export const MODERATION_ACTION_LABELS: Record<ModerationAction, string> = {
  approve: 'Approve',
  reject: 'Reject',
  ban: 'Ban',
  to_fix: 'Send to fix',
};

// ---------------------------------------------------------------------------
// Display dictionaries
// ---------------------------------------------------------------------------

export const PHOTO_STATES: PhotoState[] = [
  'uploaded',
  'ready',
  'approved',
  'rejected',
  'banned',
];

export const PHOTO_STATE_LABELS: Record<PhotoState, string> = {
  uploaded: 'Uploaded',
  ready: 'Ready',
  approved: 'Approved',
  rejected: 'Rejected',
  banned: 'Banned',
  to_fix: 'To fix',
};

export const PHOTOGRAPHER_STATES: PhotographerState[] = [
  'invalid',
  'valid',
  'banned-temporarily',
  'banned-permanently',
];

/** Active admin choices; legacy to_fix values remain readable internally. */
export const PHOTOGRAPHER_MODERATION_STATES: PhotographerState[] = [
  'invalid',
  'valid',
  'banned-temporarily',
  'banned-permanently',
];

export const PHOTOGRAPHER_STATE_LABELS: Record<PhotographerState, string> = {
  valid: 'Approved',
  'banned-temporarily': 'Temporarily banned',
  'banned-permanently': 'Permanently banned',
  to_fix: 'To fix',
  invalid: 'Unapproved',
};

export const PHOTOGRAPHER_TIER_LABELS: Record<PhotographerTier, string> = {
  basic: 'Basic',
  gold: 'Gold',
};

export interface PhotographerDetails {
  biography: string;
  websites: string[];
  banCount: number | null;
  categories: string[];
  themes: string[];
  agents: string[];
  email: string | null;
  contact: string | null;
  contactStatus: 'available' | 'missing' | 'unavailable';
}
